import { gtag, sanitizeAnalyticsUrl } from '@worldcoo/ui/lib/analytics';

/**
 * How long a page_view waits for the arriving page to write its title before being sent anyway.
 *
 * Normally reached only by a navigation with nothing to wait for, where the value is already right and
 * this is pure latency: the arriving title is IDENTICAL to the one on screen (two portal pages sharing
 * a title, a search-param change on the same page), or it was written before the history subscriber
 * even ran.
 *
 * A page whose title takes LONGER than this to arrive is reported with the previous one, which is the
 * one thing waiting cannot fix — the ambiguity is inherent, since a title that has not changed yet and
 * a title that will never change look the same from here. Portal pages resolve their title client-side
 * and are the ones at risk (TEC-3865); the hit is late-and-wrong only for that navigation, and cannot
 * affect the next one.
 */
const TITLE_SETTLE_TIMEOUT_MS = 1000;

/**
 * Sends the GA4 `page_view` of every in-app navigation, once the arriving page's title is in the
 * document (TEC-3823).
 *
 * WHAT WAS WRONG. This used to be a bare `router.history.subscribe` that read `document.title` in the
 * callback. That callback runs synchronously on the history change, before React has rendered the new
 * route, while `<Seo>` writes the title from an effect — so every hit carried the title of the page the
 * visitor came FROM. Measured on the landing before the fix: navigating to `/nope` reported
 * `page_title: "Contacto - Worldcoo"` while the document read `Página no encontrada - Worldcoo`.
 *
 * WHY THIS IS NOT AN EFFECT ABOVE THE ROUTE TREE, which is the obvious fix — React flushes passive
 * effects child-first, so an effect in a component wrapping the routes runs after `<Seo>`'s. That
 * ordering is real but it only holds WITHIN one commit, and a navigation is not one commit: the router
 * commits the location while the arriving route is still resolving its chunk, and the route subtree —
 * `<Seo>` with it — commits later. Built that way first and measured: the wrapper's effect pushed the
 * hit at t=47378.9ms and the title was written at t=47546.9ms, 168ms afterwards, still one page behind.
 *
 * WHAT THIS DOES INSTEAD. Waits for the title itself, which is the thing the hit needs, rather than for
 * any proxy of it: a navigation records the title the document has AT THAT MOMENT and sends as soon as
 * a head mutation changes it, at the latest after {@link TITLE_SETTLE_TIMEOUT_MS}. Nothing here reads
 * router or React internals, so no framework upgrade can quietly restore the old behaviour.
 *
 * WHY THE BASELINE IS PER NAVIGATION and not the title the last hit reported, which is how this first
 * shipped and is worse than the bug it replaced. A single module-scoped "last reported title" cannot
 * tell "the arriving page's title is already in the document" apart from "the previous page's title
 * landed after we reported it": once one navigation loses the race and goes out on the timeout with the
 * old title, the title it stored is stale forever after, so the NEXT navigation sees a difference that
 * belongs to its predecessor and reports the page being left — which stores a stale title again. That
 * self-sustains for the rest of the session. Measured in the browser: a hit left on the timeout with
 * `document.title` still stale 2082ms after the navigation, and every title after it one page behind.
 * Keying on the navigation removes the carry-over: nothing a navigation observes can outlive it.
 *
 * EXACTLY ONE HIT PER PAGE VIEW.
 *   * The FIRST page view is not sent here at all — `gtag('config', …)` in `router.tsx` auto-sends it,
 *     with the title already correct because it runs at hydration, after the SSR document's `<title>`.
 *   * Each later navigation sends exactly one, whichever of the three triggers comes first: the title
 *     changing, the timeout, or the next navigation (which flushes the pending hit before starting its
 *     own, so a visitor clicking twice inside the window loses neither). A `pagehide` flushes too, so
 *     leaving the page mid-window does not drop it.
 *
 * CONSENT IS UNCHANGED, and nothing here may change it: `gtag()` only pushes onto `dataLayer`, and the
 * gate is on the LOADER (`loadGtag`, called from `__root.tsx` only once the CMP grants analytics). A
 * visitor who denies, or never answers, leaves these hits in a queue that is never drained and never
 * sent. See `loadGtag`.
 */
export function installPageViewTracking(subscribeToNavigations: (onNavigate: (href: string) => void) => void): void {
	let pending: { href: string; stopWaiting: () => void } | undefined;

	const send = (href: string): void => {
		pending?.stopWaiting();
		pending = undefined;
		gtag('event', 'page_view', {
			// The history location, path + search + hash, exactly as the previous implementation
			// reported it. Not the router's own `state.location`, which is the REWRITTEN internal path
			// (`/www/contact` for `/contacto`) and would silently change every page_location in GA4.
			page_location: sanitizeAnalyticsUrl(href),
			page_title: document.title,
		});
	};

	window.addEventListener('pagehide', () => {
		if (pending) send(pending.href);
	});

	subscribeToNavigations((href) => {
		if (pending) send(pending.href);
		// The title this navigation has to beat: whatever the document reads once the page being left is
		// already gone. Scoped to this callback, so a navigation that ends on the timeout cannot mislead
		// the next one.
		const titleBeforeArrival = document.title;
		// No `titlechange` event exists, so the arrival of the new title is observed on the head. The
		// observer lives only for this window, and every mutation `<Seo>` makes lands in it.
		const observer = new MutationObserver(() => {
			if (document.title !== titleBeforeArrival) send(href);
		});
		observer.observe(document.head, { subtree: true, childList: true, characterData: true });
		const timer = window.setTimeout(() => send(href), TITLE_SETTLE_TIMEOUT_MS);
		pending = {
			href,
			stopWaiting: () => {
				observer.disconnect();
				window.clearTimeout(timer);
			},
		};
	});
}
